/* eslint-disable prefer-object-spread */
import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../../../data/axiosInstance/axiosInstance';
import executor from '../../../data/axiosInstance/executor';

const API_URL = `${process.env.REACT_APP_API_SINISTRI_URL}`;

const initialState = {
  loaderCatastrofali: false,
  loaderGrigliaSinistri: false,
  listaEventiCatastrofali: [],
  singleEventoCatastrofali: {},
  selectEventValue: null,
  listaSinitsriCatastrofali: [],
  // Righe task dei sinistri dell'evento, recuperate dal BE per id (OSSQ-2381):
  // NON si filtra piu' la griglia lavorabili lato client, che dalla paginazione
  // server-side in poi contiene solo la pagina corrente.
  taskCatastrofali: [],
};

export const EventiCatastrofaliSlice = createSlice({
  name: 'eventiCatastrofali',
  initialState,
  reducers: {
    setLoaderCatastrofali: (state, action) => {
      state.loaderCatastrofali = action.payload;
    },
    setLoaderGrigliaSinistri: (state, action) => {
      state.loaderGrigliaSinistri = action.payload;
    },
    getListEvents: (state, action) => {
      state.listaEventiCatastrofali = action.payload;
    },
    setListaSinistriCatastrofali: (state, action) => {
      state.listaSinitsriCatastrofali = action.payload;
    },
    setTaskCatastrofali: (state, action) => {
      state.taskCatastrofali = action.payload || [];
    },
    setSelectEventValue : (state, action) => {
      state.selectEventValue = action.payload;
    },
    setSingleEventoCatastrofali: (state, action) => {
      state.singleEventoCatastrofali = action.payload;
    },
  },
});

export const {
  setLoaderCatastrofali,
  setLoaderGrigliaSinistri,
  getListEvents,
  setListaSinistriCatastrofali,
  setTaskCatastrofali,
  setSelectEventValue,
  setSingleEventoCatastrofali,
} = EventiCatastrofaliSlice.actions;

export const fetchListaEventiCatastrofali = (filtri = {}) => async (dispatch) => {
  dispatch(getListEvents([]));
  dispatch(setLoaderCatastrofali(true));

  const params = new URLSearchParams();
  if (filtri.tipo) params.append('tipo', filtri.tipo);
  if (filtri.data) params.append('data', filtri.data);
  if (filtri.nazione) params.append('nazione', filtri.nazione);
  if (filtri.regione) params.append('regione', filtri.regione);
  if (filtri.comune) params.append('comune', filtri.comune);

  const queryString = params.toString();
  const API_URI_CALL = `${API_URL}/api/v1/bff/eventoCatastrofale/lista${
    queryString ? `?${queryString}` : ''
  }`;

  const fetchData = async () => axiosInstance.get(API_URI_CALL);

  const [res, err] = await executor.executeAsync(fetchData);

  if (err) {
    dispatch(setLoaderCatastrofali(false));
    return true;
  }

  dispatch(setLoaderCatastrofali(false));
  return dispatch(getListEvents(res.data));
};

export const fetchSingleEventById = (id) => async (dispatch) => {
  dispatch(setLoaderCatastrofali(true));
  const API_URI_CALL = `${API_URL}/api/v1/bff/eventoCatastrofale/${id}`;

  const fetchData = async () => axiosInstance.get(API_URI_CALL);

  const [res, err] = await executor.executeAsync(fetchData);

  console.log('res', res);
  console.log('err', err);
  if (err) return true;

  dispatch(setLoaderCatastrofali(false));
  return dispatch(setSingleEventoCatastrofali(res.data));
};

/**
 * Recupera dal BE le righe task dei sinistri di un evento catastrofale.
 *
 * <p>OSSQ-2381: la griglia dei sinistri catastrofali otteneva le colonne
 * incrociando la lista dell'evento con ticketsLavorabili. Da quando la lista
 * lavorabili e' paginata server-side quello stato contiene solo la pagina
 * corrente (50 righe), quindi i sinistri fuori pagina sparivano dalla griglia
 * anche se il BE li restituiva. Qui si chiedono esplicitamente al BE per id,
 * usando il filtro idTask della ricerca ibrida (CSV, gate lato SP).
 *
 * @param {Array} sinistri lista sinistri dell'evento (serve solo il campo id)
 */
export const fetchTaskCatastrofaliByIds = (sinistri) => async (dispatch) => {
  // Il gate della SP confronta l'id con DUE forme: CONVERT(varchar, tch_codint)
  // (numerico) oppure tnm.TaskId (prefissato). Si mandano entrambe, ma la forma
  // numerica si ricava SOLO dal suffisso dopo l'ultimo '-' e solo se e' fatto
  // di sole cifre: una regex \d+ generica su un UUID (es. l'id di un evento,
  // 43f7d78b-...) estrarrebbe "43", che potrebbe combaciare con il tch_codint
  // di un task qualunque e far comparire in griglia righe che non c'entrano.
  // Meglio nessuna riga che righe sbagliate.
  const ids = [];
  (sinistri || []).forEach((s) => {
    const grezzo = s?.id;
    if (!grezzo) return;
    const testo = String(grezzo);
    ids.push(testo);
    const suffisso = testo.split('-').pop();
    if (suffisso && suffisso !== testo && /^\d+$/.test(suffisso)) {
      ids.push(suffisso);
    }
  });
  if (ids.length === 0) {
    return dispatch(setTaskCatastrofali([]));
  }

  // Gli id viaggiano in query string come CSV: un evento con molti sinistri
  // supererebbe il limite di lunghezza dell'URL (414). Si spezza in blocchi e
  // si concatenano i risultati. size = dimensione del blocco, perche' serve
  // l'intero blocco e non i primi 50.
  // 100 id per blocco: ogni id ne produce fino a due (grezzo + numerico).
  const CHUNK_SIZE = 100;
  const chunks = [];
  for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
    chunks.push(ids.slice(i, i + CHUNK_SIZE));
  }

  const risultati = await Promise.all(
    chunks.map(async (chunk) => {
      const params = new URLSearchParams({
        idTask: chunk.join(','),
        page: '0',
        size: String(chunk.length),
      });
      const API_URI_CALL = `${API_URL}/api/v1/bff/task/lista-lavorabili/search?${params.toString()}`;
      const fetchData = async () => axiosInstance.get(API_URI_CALL);
      const [res, err] = await executor.executeAsync(fetchData);
      if (err) {
        console.warn('fetchTaskCatastrofaliByIds failed', err);
        return [];
      }
      if (Array.isArray(res?.data?.content)) return res.data.content;
      if (Array.isArray(res?.data)) return res.data;
      return [];
    })
  );

  return dispatch(setTaskCatastrofali(risultati.flat()));
};

export const fetchListaSinistriCatastrofali = (idEvento, isCodiceLob) => async (dispatch) => {  
  dispatch(setLoaderGrigliaSinistri(true));
  dispatch(setListaSinistriCatastrofali([]));

  if(idEvento !== null && idEvento !== undefined && idEvento !== ''){
    const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/sinitri/eventoCatastrofale/${idEvento}/lob/${isCodiceLob}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);
    const [res, err] = await executor.executeAsync(fetchData);
    
    if (err){
      console.log('err', err);
      dispatch(setLoaderGrigliaSinistri(false));
      return true;
    } 
    if(res){
      console.log('res', res);
      dispatch(setLoaderGrigliaSinistri(false));
      dispatch(setListaSinistriCatastrofali(res.data));
      // Le righe task servono per le colonne della griglia: si chiedono al BE
      // per id, invece di cercarle nella pagina corrente dei lavorabili.
      dispatch(fetchTaskCatastrofaliByIds(res.data));
      return true;
    }
    
  }
 
  return dispatch(setLoaderGrigliaSinistri(false));
};

export default EventiCatastrofaliSlice.reducer;
